import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { VersionsListItem } from 'src/features/plan-version-sidebar/versions-list-item';
import Collapse, { Panel } from 'src/shared/components/collapse/collapse';
import { useStore } from 'src/store';
import type { TPlanVersion, TArchivedPlansGroup } from 'src/store/comparison/types';
import { PlanVersionType } from 'src/store/comparison/types';
import {
  GOPLAN_DRILLING_CARPET_DELETE_TEMP_DRAFT,
  GOPLAN_DRILLING_CARPET_RENAME_TEMP_DRAFT,
  GOPLAN_DRILLING_CARPET_SEND_TO_INTEGRATION,
} from 'src/store/roles-store/roles';

import styles from './archived-versions-group.module.scss';

type Props = {
  versionsGroup: TArchivedPlansGroup;
  onDeletePlanVersion(planVersion: TPlanVersion): void;
  onVersionChange(planVersion: TPlanVersion): void;
  onMakeIntegrated?(planVersion: TPlanVersion): void;
};

export const ArchivedVersionsGroup = observer(function ArchivedVersionsGroup({
  versionsGroup,
  onDeletePlanVersion,
  onVersionChange,
  onMakeIntegrated,
}: Props) {
  const { t } = useTranslation();
  const { notifications, planVersion, rolesStore } = useStore();

  const currentVersionId = planVersion.version?.id;

  if (!versionsGroup.months.length) {
    return null;
  }

  const handleDeleteVersion = (version: TPlanVersion) => {
    if (version.data.availableForDashboardLite) {
      notifications.showErrorMessage(t('drawers:planVersion.deleteAnalyticsVersionError'));
    } else {
      onDeletePlanVersion(version);
    }
  };

  return (
    <Collapse key={versionsGroup.year} defaultActiveKey={versionsGroup.year}>
      <Panel key={versionsGroup.year} header={versionsGroup.year} className={styles.text}>
        {versionsGroup.months.map((monthGroup) => (
          <Collapse key={monthGroup.month} defaultActiveKey={monthGroup.month}>
            <Panel key={monthGroup.month} header={t(monthGroup.month)} className={styles.text}>
              <ul className={styles.versionsList}>
                {monthGroup.plans.map((plan) => {
                  const isDeletingAvailable =
                    rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_DELETE_TEMP_DRAFT]) && currentVersionId !== plan.id;
                  const isRenamingAvailable =
                    plan.data.versionType !== PlanVersionType.draft &&
                    rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_RENAME_TEMP_DRAFT]);
                  const integrationAvailable =
                    plan.data.versionType !== PlanVersionType.public &&
                    rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_SEND_TO_INTEGRATION]);

                  return (
                    <VersionsListItem
                      key={plan.id}
                      version={plan}
                      isRenamingAvailable={isRenamingAvailable}
                      onRename={isRenamingAvailable ? planVersion.renameVersion : undefined}
                      onDeleteVersion={isDeletingAvailable ? handleDeleteVersion : undefined}
                      onVersionChange={onVersionChange}
                      onMakeIntegrated={integrationAvailable ? onMakeIntegrated : undefined}
                    />
                  );
                })}
              </ul>
            </Panel>
          </Collapse>
        ))}
      </Panel>
    </Collapse>
  );
});
